import { alpha, Theme, ThemeOptions } from '@mui/material/styles';

// iOS Safari zooms the page when focusing a field whose font-size is < 16px
// and never zooms back out. Keep 14px on desktop, use 16px on touch devices.
const touchDeviceMedia = '@media (pointer: coarse)';

/** Radius shared by every container surface: cards, tables, dialogs, drawers. */
export const SURFACE_RADIUS = 20;
/** Radius for floating surfaces: menus, popovers, autocomplete lists. */
export const POPOVER_RADIUS = 16;
/** Pill radius for inputs, matching the buttons. */
export const INPUT_RADIUS = 20;
export const INPUT_HEIGHT = 36;
/**
 * Class for table columns holding buttons, avatars or checkboxes: the column
 * shrinks to its content instead of taking a share of the table width.
 */
export const FIT_CELL_CLASS = 'EvoTableCell-fit';

/**
 * Surface tokens, exposed as `theme.palette.surface`. Components should read
 * these instead of picking a `shade` per mode, so a color change happens here
 * once.
 */
const getSurfaceTokens = (theme: Theme) => {
  const { shade, mode } = theme.palette;

  if (mode === 'light') {
    return {
      page: '#FAFAFA',
      paper: shade?.[50] ?? '#FFFFFF',
      input: '#F2F2F2',
      inputBorder: '#01070C1f',
      tableHead: '#EDEDED',
      divider: '#01070C1f',
      shadow: '0px 1px 2px 0px #00000029',
      popoverShadow: '0px 4px 16px 0px #0000001f',
      // Edge between the navbar/sidebar and the page.
      chromeBorder: '1px solid #01070C1f',
      // Neutral actions ("Cancelar" outline).
      neutralAction: '#616161',
    };
  }

  return {
    page: shade?.[50] ?? '#01070C',
    paper: shade?.[200] ?? '#091016',
    input: shade?.[400] ?? '#162436',
    inputBorder: 'transparent',
    tableHead: shade?.[300] ?? '#0C1822',
    divider: '#ffffff1f',
    shadow: 'none',
    popoverShadow: 'none',
    chromeBorder: 'none',
    neutralAction: '#FFFFFF',
  };
};

export type SurfaceTokens = ReturnType<typeof getSurfaceTokens>;

/**
 * Shared look for every page outside the viewer, modelled after Evotally:
 * page background + raised paper surfaces with a 20px radius, pill inputs on
 * a soft fill, and table headers in a subtle tint.
 */
const getAppSurfaces = (theme: Theme): ThemeOptions => {
  const surface = getSurfaceTokens(theme);
  const { primary, mode } = theme.palette;
  const isDark = mode === 'dark';
  const hover = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(1, 7, 12, 0.05)';

  const inputRoot = {
    height: INPUT_HEIGHT,
    borderRadius: INPUT_RADIUS,
    backgroundColor: surface.input,
    border: 'none',
    '& .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${surface.inputBorder}`,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${primary.main}`,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${primary.main}`,
    },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.error.main,
    },
    '&.Mui-disabled': {
      opacity: 0.7,
      '& .MuiOutlinedInput-notchedOutline': { border: `1px solid ${surface.inputBorder}` },
    },
    '&.MuiInputBase-multiline': {
      height: 'auto',
      borderRadius: SURFACE_RADIUS / 2,
    },
  };

  return {
    palette: {
      background: { default: surface.page, paper: surface.paper },
      divider: surface.divider,
      action: { hover },
      surface,
    },
    components: {
      // Tokens for plain-CSS consumers (e.g. the report editor's editor.css).
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--evo-editor-desk': surface.tableHead,
            '--evo-editor-page-shadow': surface.shadow,
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            background: surface.paper,
            boxShadow: 'none',
            borderBottom: surface.chromeBorder,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundColor: surface.paper, backgroundImage: 'none' },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: SURFACE_RADIUS,
            backgroundColor: surface.paper,
            backgroundImage: 'none',
            boxShadow: surface.shadow,
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${surface.divider}` },
          title: { fontSize: '1rem', fontWeight: 500 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: SURFACE_RADIUS,
            backgroundColor: surface.paper,
            backgroundImage: 'none',
            boxShadow: surface.popoverShadow,
            [theme.breakpoints.down('sm')]: {
              '&.MuiDialog-paperFullScreen': { borderRadius: 0 },
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { fontSize: '1.125rem', fontWeight: 600 } },
      },
      MuiDialogActions: {
        styleOverrides: { root: { padding: '16px 24px', gap: '8px' } },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: surface.paper,
            backgroundImage: 'none',
            boxShadow: surface.popoverShadow,
          },
          // Only floating drawers (right/bottom) get the rounded edge; the
          // left one is the navigation sidebar.
          paperAnchorRight: {
            [theme.breakpoints.up('md')]: {
              borderRadius: `${SURFACE_RADIUS}px 0 0 ${SURFACE_RADIUS}px`,
            },
          },
          paperAnchorBottom: {
            borderRadius: `${SURFACE_RADIUS}px ${SURFACE_RADIUS}px 0 0`,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: POPOVER_RADIUS,
            backgroundColor: surface.paper,
            boxShadow: surface.popoverShadow,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            borderRadius: POPOVER_RADIUS,
            boxShadow: surface.popoverShadow,
          },
          // Same 36px pill as other inputs; grows only when chips wrap.
          inputRoot: {
            height: 'auto',
            minHeight: INPUT_HEIGHT,
            '&.MuiOutlinedInput-root': {
              paddingTop: 2,
              paddingBottom: 2,
              paddingLeft: 9,
            },
            '& .MuiAutocomplete-input.MuiOutlinedInput-input': {
              padding: '5.5px 4px',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            // Pill radius (base theme) turns multi-line items into blobs.
            borderRadius: 12,
            '&:hover': { backgroundColor: hover },
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: alpha(primary.main, isDark ? 0.24 : 0.12),
            },
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: SURFACE_RADIUS,
            backgroundColor: surface.paper,
            boxShadow: surface.shadow,
          },
        },
      },
      MuiTable: {
        styleOverrides: { root: { backgroundColor: surface.paper } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            [`&.${FIT_CELL_CLASS}`]: { width: '1%', whiteSpace: 'nowrap' },
          },
          head: {
            backgroundColor: surface.tableHead,
            color: theme.palette.text.primary,
            fontWeight: 600,
            textTransform: 'none',
          },
          body: {
            backgroundColor: 'transparent',
            borderBottom: `1px solid ${surface.divider}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:last-of-type td': { borderBottom: 'none' },
            '&.MuiTableRow-hover:hover': { backgroundColor: hover },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: { root: { backgroundColor: 'transparent' } },
      },
      MuiTabs: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${surface.divider}` },
        },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontSize: '0.875rem' } },
      },
      MuiOutlinedInput: {
        styleOverrides: { root: inputRoot },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            height: INPUT_HEIGHT,
            backgroundColor: surface.input,
            border: 'none',
          },
          select: {
            fontSize: '14px',
            [touchDeviceMedia]: { fontSize: '16px' },
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            height: INPUT_HEIGHT,
            backgroundColor: surface.input,
            border: `1px solid ${surface.inputBorder}`,
            '&:hover:not(.Mui-disabled)': { borderColor: primary.main },
            '&.Mui-focused': { borderColor: primary.main },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            fontSize: '14px',
            [touchDeviceMedia]: { fontSize: '16px' },
          },
        },
      },
      // Date/time pickers render their own field instead of OutlinedInput.
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            ...inputRoot,
            '& .MuiPickersOutlinedInput-notchedOutline': {
              border: `1px solid ${surface.inputBorder}`,
            },
            '&:hover .MuiPickersOutlinedInput-notchedOutline': {
              border: `1px solid ${primary.main}`,
            },
            '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
              border: `1px solid ${primary.main}`,
            },
          },
        },
      },
      MuiPickersSectionList: {
        styleOverrides: { root: { padding: '8px 0', fontSize: '14px' } },
      },
      MuiButton: {
        variants: [
          // Secondary outlined buttons ("Cancelar"): a neutral outline, white
          // on dark and grey on light, so they never compete with the primary.
          {
            props: { variant: 'outlined', color: 'secondary' },
            style: {
              border: `1px solid ${surface.neutralAction}`,
              color: surface.neutralAction,
              '&:hover': {
                border: `1px solid ${surface.neutralAction}`,
                color: surface.neutralAction,
                backgroundColor: hover,
                opacity: 1,
              },
            },
          },
          // Disabled buttons use MUI's neutral disabled colors (as in
          // Evotally) instead of each variant's own grey. `background` also
          // clears the contained variants' gradients.
          {
            props: { variant: 'contained' },
            style: {
              '&.Mui-disabled': {
                background: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
                boxShadow: 'none',
              },
            },
          },
          {
            props: { variant: 'outlined' },
            style: {
              '&.Mui-disabled': {
                border: `1px solid ${theme.palette.action.disabledBackground}`,
                color: theme.palette.action.disabled,
              },
            },
          },
          {
            props: { variant: 'text' },
            style: {
              '&.Mui-disabled': { color: theme.palette.action.disabled },
            },
          },
        ],
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: surface.divider } },
      },
    },
  } as ThemeOptions;
};

export default getAppSurfaces;
